import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CRMPermissionsService } from "@/services/crm-permissions";
import { ObjectProfilePermission, PermissionFormData, PermissionFormSchema } from "@/types/crm/permissions";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

interface PermissionModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    permission: ObjectProfilePermission;
    referenceTypeId: number;
    onSuccess: () => void;
    selectedObjectName?: string;
}

export function PermissionModal({ open, onOpenChange, permission, referenceTypeId, onSuccess, selectedObjectName }: PermissionModalProps) {
    const [loading, setLoading] = useState(false);
    const [availableProfiles, setAvailableProfiles] = useState<string[]>([]);
    const [selectedSourceProfile, setSelectedSourceProfile] = useState<string>("");
    const [copyLoading, setCopyLoading] = useState(false);

    const form = useForm<PermissionFormData>({
        resolver: zodResolver(PermissionFormSchema),
        defaultValues: {
            reference_type: referenceTypeId,
            profile: permission.profile,
            action_list: permission.action_list || "none",
            action_retrieve: permission.action_retrieve || "none",
            action_create: permission.action_create || "none",
            action_update: permission.action_update || "none",
            action_delete: permission.action_delete || "none",
            custom_actions: permission.custom_actions || [],
            use_hierarchy: permission.use_hierarchy || false
        }
    });

    useEffect(() => {
        if (open) {
            // Éviter les resets multiples en utilisant un délai
            const timer = setTimeout(() => {
                form.reset({
                    reference_type: referenceTypeId,
                    profile: permission.profile,
                    action_list: permission.action_list || "none",
                    action_retrieve: permission.action_retrieve || "none",
                    action_create: permission.action_create || "none",
                    action_update: permission.action_update || "none",
                    action_delete: permission.action_delete || "none",
                    custom_actions: permission.custom_actions || [],
                    use_hierarchy: permission.use_hierarchy || false
                });
            }, 10);

            return () => clearTimeout(timer);
        }
    }, [open, permission.id, referenceTypeId]); // Utiliser permission.id au lieu de l'objet complet

    // Récupérer les profils disponibles pour la copie
    useEffect(() => {
        if (open) {
            const fetchAvailableProfiles = async () => {
                try {
                    const permissions = await CRMPermissionsService.getPermissions(referenceTypeId);
                    const profiles = permissions
                        .map(p => p.profile)
                        .filter(profile => profile !== permission.profile) // Exclure le profil actuel
                        .sort();
                    setAvailableProfiles(profiles);
                } catch (error) {
                    console.error("Error fetching available profiles:", error);
                }
            };
            fetchAvailableProfiles();
        }
    }, [open, referenceTypeId, permission.profile]);

    const onSubmit = async (data: PermissionFormData) => {
        setLoading(true);

        try {
            await CRMPermissionsService.updateOrCreatePermission(referenceTypeId, permission.profile, data);
            toast.success("Permission updated");
            onSuccess();
            onOpenChange(false);
        } catch (error) {
            toast.error("Error saving permission");
        } finally {
            setLoading(false);
        }
    };

    const handleCopyPermissions = async () => {
        if (!selectedSourceProfile) {
            toast.error("Please select a source profile");
            return;
        }

        setCopyLoading(true);

        try {
            const permissions = await CRMPermissionsService.getPermissions(referenceTypeId);
            const sourcePermission = permissions.find(p => p.profile === selectedSourceProfile);

            if (!sourcePermission) {
                toast.error("Source profile not found");
                return;
            }

            // Mettre à jour le formulaire avec les permissions du profil source
            form.reset({
                reference_type: referenceTypeId,
                profile: permission.profile,
                action_list: sourcePermission.action_list || "none",
                action_retrieve: sourcePermission.action_retrieve || "none",
                action_create: sourcePermission.action_create || "none",
                action_update: sourcePermission.action_update || "none",
                action_delete: sourcePermission.action_delete || "none",
                custom_actions: sourcePermission.custom_actions || [],
                use_hierarchy: sourcePermission.use_hierarchy || false
            });

            toast.success(`Permissions copied from the profile "${selectedSourceProfile}"`);
            setSelectedSourceProfile(""); // Réinitialiser la sélection
        } catch (error) {
            toast.error("Error copying permissions");
        } finally {
            setCopyLoading(false);
        }
    };

    // Vérifier si au moins une permission est "private" - optimisé pour éviter les re-renders
    const watchedPermissions = form.watch(["action_list", "action_retrieve", "action_create", "action_update", "action_delete"]);
    const hasPrivatePermission = useMemo(() => watchedPermissions.some(value => value === "private"), [watchedPermissions]);

    // Helper function pour gérer les changements de permissions de façon optimisée
    const handlePermissionChange = useCallback(
        (fieldName: keyof PermissionFormData, value: string) => {
            form.setValue(fieldName, value, { shouldValidate: true });
        },
        [form]
    );

    // Helper function pour sélectionner toutes les permissions d'une colonne
    const handleSelectAllColumn = useCallback(
        (permissionValue: string) => {
            const allActions: (keyof PermissionFormData)[] = ["action_list", "action_retrieve", "action_create", "action_update", "action_delete"];

            allActions.forEach(action => {
                handlePermissionChange(action, permissionValue);
            });
        },
        [handlePermissionChange]
    );

    // Helper function pour créer les checkbox de permissions - mémorisée pour éviter les re-renders
    const renderPermissionCheckbox = useCallback(
        (fieldName: keyof PermissionFormData, permissionValue: string) => (
            <FormField
                key={`${fieldName}-${permissionValue}`}
                control={form.control}
                name={fieldName}
                render={({ field }) => (
                    <FormControl>
                        <Checkbox
                            checked={field.value === permissionValue}
                            onCheckedChange={checked => {
                                if (checked) handlePermissionChange(fieldName, permissionValue);
                            }}
                        />
                    </FormControl>
                )}
            />
        ),
        [form.control, handlePermissionChange]
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {selectedObjectName || "Object"} permissions for {permission.profile}
                    </DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        {/* Section de copie des permissions */}
                        {availableProfiles.length > 0 && (
                            <div className="rounded-lg border bg-gray-50 p-4">
                                <h3 className="mb-3 text-sm font-medium text-gray-900">Copy permissions from another profile</h3>
                                <div className="flex gap-2">
                                    <Select value={selectedSourceProfile} onValueChange={setSelectedSourceProfile}>
                                        <SelectTrigger className="w-64">
                                            <SelectValue placeholder="Select a source profile" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableProfiles.map(profile => (
                                                <SelectItem key={profile} value={profile}>
                                                    {profile}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button type="button" variant="outline" onClick={handleCopyPermissions} disabled={!selectedSourceProfile || copyLoading} className="whitespace-nowrap">
                                        {copyLoading ? "Copy..." : "Copy permissions"}
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="rounded-l p-3 text-left text-sm font-medium">Action</th>
                                            <th className="p-2 text-center text-sm font-medium">
                                                <div className="flex flex-col">
                                                    <span>None</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectAllColumn("none")}
                                                        className=" cursor-pointer text-xs text-blue-600 underline-offset-4 hover:underline"
                                                    >
                                                        Select all
                                                    </button>
                                                </div>
                                            </th>
                                            <th className="p-2 text-center text-sm font-medium">
                                                <div className="flex flex-col">
                                                    <span>Private</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectAllColumn("private")}
                                                        className=" cursor-pointer text-xs text-blue-600 underline-offset-4 hover:underline"
                                                    >
                                                        Select all
                                                    </button>
                                                </div>
                                            </th>
                                            <th className="rounded-r p-2 text-center text-sm font-medium">
                                                <div className="flex flex-col">
                                                    <span>All</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectAllColumn("public")}
                                                        className=" cursor-pointer text-xs text-blue-600 underline-offset-4 hover:underline"
                                                    >
                                                        Select all
                                                    </button>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-b">
                                            <td className="p-3 text-sm font-medium">List View & search</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_list", "none")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_list", "private")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_list", "public")}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 text-sm font-medium">Detail View</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_retrieve", "none")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_retrieve", "private")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_retrieve", "public")}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 text-sm font-medium">Create a record</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_create", "none")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_create", "private")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_create", "public")}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 text-sm font-medium">Update a record</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_update", "none")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_update", "private")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_update", "public")}</td>
                                        </tr>
                                        <tr>
                                            <td className="p-3 text-sm font-medium">Delete a record</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_delete", "none")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_delete", "private")}</td>
                                            <td className="p-3 text-center text-sm">{renderPermissionCheckbox("action_delete", "public")}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Checkbox pour la hiérarchie - toujours visible pour éviter les sauts de layout */}
                        <FormField
                            control={form.control}
                            name="use_hierarchy"
                            render={({ field }) => (
                                <FormItem className={`space-y-2 transition-opacity duration-200 ${!hasPrivatePermission ? "pointer-events-none opacity-50" : ""}`}>
                                    <div className="flex items-center space-x-2">
                                        <FormControl>
                                            <Checkbox
                                                checked={hasPrivatePermission ? field.value : false}
                                                onCheckedChange={hasPrivatePermission ? field.onChange : undefined}
                                                disabled={!hasPrivatePermission}
                                            />
                                        </FormControl>
                                        <FormLabel className="text-sm font-medium">Use hierarchy</FormLabel>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        When enabled, private permissions respect the organizational hierarchy (the user only sees objects from their team and subordinate teams).
                                        {!hasPrivatePermission && <span className="mt-1 block text-amber-600">This option is only available when at least one permission is set to "Private".</span>}
                                    </p>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={loading}>
                                {loading ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
